/**
 * OneKey Wallet SDK Integration
 * Provides signing functionality using OneKey wallet when OneKey wallet is connected
 * Supports Ethereum, Solana, and Bitcoin chains
 * 
 * Reference: https://github.com/OneKeyHQ/app-monorepo
 * 
 * OneKey wallet structure:
 * - window.$onekey.ethereum - Ethereum provider
 * - window.$onekey.solana - Solana provider
 * - window.$onekey.btc - Bitcoin provider (native)
 * - window.unisat - Bitcoin provider (backward compatibility, provided by OneKey)
 */

/**
 * OneKey Ethereum signing result
 */
export interface OneKeyEthereumSignResult {
  signature: string;
  address: string;
}

/**
 * OneKey Solana signing result
 */
export interface OneKeySolanaSignResult {
  signature: string;
  publicKey: string;
}

/**
 * OneKey Bitcoin signing result
 */
export interface OneKeyBitcoinSignResult {
  signature: string;
  address: string;
}

/**
 * Check if OneKey wallet is installed and available
 */
export function isOneKeyWalletAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).$onekey;
}

/**
 * Check if OneKey Ethereum provider is available
 */
export function isOneKeyEthereumAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Primary: Check $onekey.ethereum
  if ((window as any).$onekey?.ethereum) {
    return true;
  }
  
  // Secondary: Check if ethereum has isOneKey flag
  if ((window as any).ethereum?.isOneKey) {
    return true;
  }
  
  return false;
}

/**
 * Check if OneKey Solana provider is available
 */
export function isOneKeySolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).$onekey?.solana;
}

/**
 * Check if OneKey Bitcoin provider is available
 * 
 * OneKey provides Bitcoin support via:
 * 1. window.$onekey.btc (native OneKey provider)
 * 2. window.unisat (backward compatibility for Unisat-integrated dApps)
 * 
 * We check for $onekey.btc first, then fallback to unisat if OneKey wallet is detected
 */
export function isOneKeyBitcoinAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Primary: Check $onekey.btc (native OneKey Bitcoin provider)
  if ((window as any).$onekey?.btc) {
    return true;
  }
  
  // Secondary: Check window.unisat if OneKey wallet is detected
  // (OneKey provides window.unisat for backward compatibility)
  if ((window as any).$onekey && (window as any).unisat) {
    return true;
  }
  
  return false;
}

/**
 * Get OneKey Ethereum provider
 */
export function getOneKeyEthereumProvider(): any {
  if (typeof window === "undefined") return null;
  
  // Primary: Check $onekey.ethereum
  if ((window as any).$onekey?.ethereum) {
    return (window as any).$onekey.ethereum;
  }
  
  // Secondary: Check if ethereum has isOneKey flag
  if ((window as any).ethereum?.isOneKey) {
    return (window as any).ethereum;
  }
  
  return null;
}

/**
 * Get OneKey Solana provider
 */
export function getOneKeySolanaProvider(): any {
  if (typeof window === "undefined") return null;
  return (window as any).$onekey?.solana || null;
}

/**
 * Get OneKey Bitcoin provider
 * 
 * Returns the native $onekey.btc provider if available,
 * otherwise returns window.unisat if OneKey wallet is detected
 */
export function getOneKeyBitcoinProvider(): any {
  if (typeof window === "undefined") return null;
  
  // Primary: Check $onekey.btc (native OneKey Bitcoin provider)
  if ((window as any).$onekey?.btc) {
    return (window as any).$onekey.btc;
  }
  
  // Secondary: Check window.unisat if OneKey wallet is detected
  // (OneKey provides window.unisat for backward compatibility)
  if ((window as any).$onekey && (window as any).unisat) {
    return (window as any).unisat;
  }
  
  return null;
}

/**
 * Get Ethereum address from OneKey wallet
 */
export async function getOneKeyEthereumAddress(): Promise<string> {
  const provider = getOneKeyEthereumProvider();
  if (!provider) {
    throw new Error("OneKey Ethereum wallet not found");
  }

  try {
    // Request accounts from OneKey wallet
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) {
      throw new Error("No accounts found in OneKey Ethereum wallet");
    }
    return accounts[0];
  } catch (error) {
    throw new Error(`Failed to get OneKey Ethereum address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign EIP-712 typed data with OneKey Ethereum wallet
 */
export async function signOneKeyEthereumTypedData(
  domain: any,
  types: any,
  message: any,
  address: string
): Promise<OneKeyEthereumSignResult> {
  const provider = getOneKeyEthereumProvider();
  if (!provider) {
    throw new Error("OneKey Ethereum wallet not found");
  }

  try {
    // Use OneKey wallet's native EIP-712 signing
    const signature = await provider.request({
      method: 'eth_signTypedData_v4',
      params: [address, JSON.stringify({
        domain,
        types,
        primaryType: 'AgentApproval',
        message
      })]
    });

    return {
      signature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OneKey Ethereum: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Sign message with OneKey Ethereum wallet
 */
export async function signOneKeyEthereumMessage(
  message: string,
  address: string
): Promise<OneKeyEthereumSignResult> {
  const provider = getOneKeyEthereumProvider();
  if (!provider) {
    throw new Error("OneKey Ethereum wallet not found");
  }

  try {
    // Use eth_sign for message signing
    const signature = await provider.request({
      method: 'eth_sign',
      params: [address, message]
    });

    return {
      signature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign message with OneKey Ethereum: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get Solana address from OneKey wallet
 */
export async function getOneKeySolanaAddress(): Promise<string> {
  const provider = getOneKeySolanaProvider();
  if (!provider) {
    throw new Error("OneKey Solana wallet not found");
  }

  try {
    // Connect to OneKey Solana wallet
    // OneKey Solana provider follows the standard Solana wallet adapter pattern
    const response = await provider.connect();
    if (!response || !response.publicKey) {
      throw new Error("Failed to connect to OneKey Solana wallet");
    }
    return response.publicKey.toString();
  } catch (error) {
    throw new Error(`Failed to get OneKey Solana address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign message with OneKey Solana wallet
 */
export async function signOneKeySolanaMessage(
  message: string | Uint8Array,
  publicKey?: string
): Promise<OneKeySolanaSignResult> {
  const provider = getOneKeySolanaProvider();
  if (!provider) {
    throw new Error("OneKey Solana wallet not found");
  }

  try {
    // Ensure connected
    if (!provider.isConnected) {
      await provider.connect();
    }

    // Convert message to Uint8Array if string
    const messageBytes = typeof message === 'string' 
      ? new TextEncoder().encode(message)
      : message;

    // Sign message with OneKey Solana wallet
    const signedMessage = await provider.signMessage(messageBytes, 'utf8');

    // Convert signature to hex string
    const signatureBytes = Array.isArray(signedMessage.signature) 
      ? signedMessage.signature as number[]
      : Array.from(signedMessage.signature as Uint8Array) as number[];
    const signatureHex = signatureBytes
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const pubKey = signedMessage.publicKey?.toString() || publicKey || '';
    if (!pubKey) {
      throw new Error("Failed to get public key from OneKey Solana wallet");
    }

    return {
      signature: signatureHex,
      publicKey: pubKey
    };
  } catch (error: any) {
    if (error.code === 4001 || error.message?.includes("rejected")) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OneKey Solana: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get Bitcoin address from OneKey wallet
 * 
 * OneKey Bitcoin uses:
 * - window.$onekey.btc (native provider) - preferred
 * - window.unisat (backward compatibility) - fallback if OneKey wallet detected
 * 
 * Both providers use the Unisat-compatible API:
 * - requestAccounts() - preferred method
 * - getAccounts() - for already connected wallets
 */
export async function getOneKeyBitcoinAddress(): Promise<string> {
  const provider = getOneKeyBitcoinProvider();
  if (!provider) {
    throw new Error("OneKey Bitcoin wallet not found");
  }

  try {
    let address: string | undefined;

    // Method 1: Try requestAccounts() (preferred)
    if (typeof provider.requestAccounts === 'function') {
      const accounts = await provider.requestAccounts();
      if (accounts && accounts.length > 0) {
        address = accounts[0];
      }
    }

    // Method 2: Try getAccounts() if already connected
    if (!address && typeof provider.getAccounts === 'function') {
      const accounts = await provider.getAccounts();
      if (accounts && accounts.length > 0) {
        address = accounts[0];
      }
    }

    if (!address) {
      throw new Error("No accounts found in OneKey Bitcoin wallet. Please ensure the wallet is connected and has a Bitcoin account.");
    }

    return address;
  } catch (error) {
    if (error instanceof Error && error.message.includes("No accounts found")) {
      throw error;
    }
    throw new Error(`Failed to get OneKey Bitcoin address: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sign message with OneKey Bitcoin wallet
 * 
 * OneKey Bitcoin uses Unisat-compatible API:
 * - signMessage(message, type) where type can be 'ecdsa' or 'bip322-simple'
 */
export async function signOneKeyBitcoinMessage(
  message: string,
  address: string,
  method: 'ecdsa' | 'bip322-simple' = 'ecdsa'
): Promise<OneKeyBitcoinSignResult> {
  const provider = getOneKeyBitcoinProvider();
  if (!provider) {
    throw new Error("OneKey Bitcoin wallet not found");
  }

  try {
    // Ensure connected - try requestAccounts() first, then getAccounts()
    let accounts: string[] = [];
    
    if (typeof provider.requestAccounts === 'function') {
      accounts = await provider.requestAccounts();
    } else if (typeof provider.getAccounts === 'function') {
      accounts = await provider.getAccounts();
    }
    
    // Verify address is available
    if (!accounts.includes(address)) {
      throw new Error("Address not found in OneKey Bitcoin wallet. Please ensure the wallet is connected and the address is available.");
    }

    // Sign message with OneKey Bitcoin wallet
    // OneKey uses Unisat-compatible API
    const signature = await provider.signMessage(message, method);

    // Normalize signature format (remove 0x prefix if present)
    let normalizedSignature = signature;
    if (signature.startsWith('0x')) {
      normalizedSignature = signature.slice(2);
    }

    return {
      signature: normalizedSignature,
      address
    };
  } catch (error: any) {
    if (error.code === 4001 || error.message?.includes("rejected")) {
      throw new Error("User rejected the signature request");
    }
    throw new Error(`Failed to sign with OneKey Bitcoin: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Connect to OneKey wallet for a specific chain
 */
export async function connectOneKeyWallet(chainType: 'ethereum' | 'solana' | 'bitcoin'): Promise<string> {
  switch (chainType) {
    case 'ethereum':
      return await getOneKeyEthereumAddress();
    case 'solana':
      return await getOneKeySolanaAddress();
    case 'bitcoin':
      return await getOneKeyBitcoinAddress();
    default:
      throw new Error(`Unsupported chain type: ${chainType}`);
  }
}
